/* ==========================================================
   ESSENCE STRICT SEARCH
   Chez Mishel / Shopify Prestige

   Goals:
   - Keep the buyer-facing URL and query exactly as typed.
   - Text search: search product/resource title + tags only.
   - Numeric/SKU-like search: preserve Shopify's native search path.
   - Keep existing search sorting and pagination behavior intact.
   - Narrow predictive search fields without replacing theme UI.
   - Fail open: if the strict render fails, show native Shopify results.
========================================================== */

(() => {
  'use strict';

  if (window.__essenceStrictSearchLoaded) {
    return;
  }

  window.__essenceStrictSearchLoaded = true;

  const SEARCH_PATH_RE = /\/search\/?$/;
  const PREDICTIVE_PATH_RE = /\/search\/suggest(?:\.json)?\/?$/;
  const SEARCH_SECTION_SELECTOR = '.shopify-section--main-search';
  const PENDING_CLASS = 'essence-strict-search-pending';
  const INTERNAL_SECTION_PARAM = 'section_id';
  const PREDICTIVE_FIELDS = 'title,tag,variants.sku';
  const MAX_QUERY_LENGTH = 180;
  const MAX_TERMS = 12;

  let renderController = null;
  let renderVersion = 0;

  const normalizeWhitespace = (value) =>
    String(value || '')
      .replace(/\s+/gu, ' ')
      .trim();

  const isSkuLikeQuery = (query) => {
    const normalized = normalizeWhitespace(query);

    return (
      normalized.length > 0 &&
      /\d/u.test(normalized) &&
      /^[\d\s._\-/]+$/u.test(normalized)
    );
  };

  const escapeSearchPhrase = (value) =>
    value
      .replace(/\\/gu, '\\\\')
      .replace(/"/gu, '\\"');

  const tokenizeTextQuery = (query) => {
    const normalized = normalizeWhitespace(query).slice(0, MAX_QUERY_LENGTH);

    if (!normalized) {
      return [];
    }

    return normalized
      .split(' ')
      .map((term) =>
        term
          .replace(/^[,.;!?()[\]{}<>]+/gu, '')
          .replace(/[,.;!?()[\]{}<>]+$/gu, '')
          .trim()
      )
      .filter(Boolean)
      .slice(0, MAX_TERMS);
  };

  const buildStrictQuery = (query) => {
    const terms = tokenizeTextQuery(query);

    if (!terms.length) {
      return '';
    }

    return terms
      .map((term) => {
        const phrase = escapeSearchPhrase(term);
        return `(title:"${phrase}" OR tag:"${phrase}")`;
      })
      .join(' AND ');
  };

  const getBuyerQuery = (url = new URL(window.location.href)) =>
    normalizeWhitespace(url.searchParams.get('q'));

  const shouldUseStrictSearch = (query) =>
    Boolean(query) && !isSkuLikeQuery(query) && Boolean(buildStrictQuery(query));

  const setPending = (isPending) => {
    document.documentElement.classList.toggle(PENDING_CLASS, Boolean(isPending));

    const section = document.querySelector(SEARCH_SECTION_SELECTOR);
    if (section) {
      if (isPending) {
        section.setAttribute('aria-busy', 'true');
      } else {
        section.removeAttribute('aria-busy');
      }
    }
  };

  const clearFailsafe = () => {
    if (window.__essenceStrictSearchFailsafe) {
      window.clearTimeout(window.__essenceStrictSearchFailsafe);
      window.__essenceStrictSearchFailsafe = null;
    }
  };

  const syncSearchInputs = (buyerQuery) => {
    const selectors = [
      '#predictive-search-form input[name="q"]',
      '.main-search-form input[name="q"]'
    ];

    document.querySelectorAll(selectors.join(',')).forEach((input) => {
      if (input instanceof HTMLInputElement && document.activeElement !== input) {
        input.value = buyerQuery;
      }
    });
  };

  const replaceTechnicalQueryInText = (element, technicalQuery, buyerQuery) => {
    if (!element || !technicalQuery) {
      return;
    }

    const currentText = element.textContent || '';

    if (currentText.includes(technicalQuery)) {
      element.textContent = currentText.split(technicalQuery).join(buyerQuery);
    }
  };

  const cleanSearchUrl = (sourceUrl, buyerQuery) => {
    const cleanUrl = new URL(sourceUrl, window.location.origin);

    cleanUrl.searchParams.delete(INTERNAL_SECTION_PARAM);
    cleanUrl.searchParams.set('q', buyerQuery);

    const activeSort = new URL(window.location.href).searchParams.get('sort_by');
    if (activeSort) {
      cleanUrl.searchParams.set('sort_by', activeSort);
    } else {
      cleanUrl.searchParams.delete('sort_by');
    }

    return cleanUrl;
  };

  const sanitizeRenderedSection = (section, technicalQuery, buyerQuery) => {
    if (!section) {
      return;
    }

    section.querySelectorAll('script').forEach((script) => script.remove());

    replaceTechnicalQueryInText(
      section.querySelector('.section-header .v-stack > p'),
      technicalQuery,
      buyerQuery
    );

    section.querySelectorAll('.empty-state .prose p').forEach((paragraph) => {
      replaceTechnicalQueryInText(paragraph, technicalQuery, buyerQuery);
    });

    section.querySelectorAll('input[name="q"]').forEach((input) => {
      if (input instanceof HTMLInputElement) {
        input.value = buyerQuery;
      }
    });

    section.querySelectorAll('a[href]').forEach((anchor) => {
      try {
        const targetUrl = new URL(anchor.href, window.location.origin);

        if (
          targetUrl.origin === window.location.origin &&
          SEARCH_PATH_RE.test(targetUrl.pathname) &&
          targetUrl.searchParams.has('q')
        ) {
          anchor.href = cleanSearchUrl(targetUrl, buyerQuery).toString();
        }
      } catch (error) {
        // Keep the original href if it can't be parsed.
      }
    });
  };

  const extractSectionHtml = (html, sectionId) => {
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const expectedId = `shopify-section-${sectionId}`;
    const wrapper = parsed.getElementById(expectedId);

    if (wrapper) {
      return wrapper.innerHTML;
    }

    const fallbackWrapper = parsed.querySelector(SEARCH_SECTION_SELECTOR);
    if (fallbackWrapper) {
      return fallbackWrapper.innerHTML;
    }

    return parsed.body ? parsed.body.innerHTML : '';
  };

  const getSearchSection = () => {
    const section = document.querySelector(SEARCH_SECTION_SELECTOR);

    if (!(section instanceof HTMLElement) || !section.id.startsWith('shopify-section-')) {
      return null;
    }

    return section;
  };

  const buildInternalRequestUrl = (buyerUrl, sectionId, technicalQuery) => {
    const requestUrl = new URL(buyerUrl.toString());

    requestUrl.hash = '';
    requestUrl.searchParams.set('q', technicalQuery);
    requestUrl.searchParams.set(INTERNAL_SECTION_PARAM, sectionId);

    // Existing Essence sorting is intentionally client-side per loaded page.
    // Keep the internal Shopify render independent from custom sort values.
    requestUrl.searchParams.delete('sort_by');

    return requestUrl;
  };

  const dispatchThemeLoadingEnd = () => {
    document.documentElement.dispatchEvent(
      new CustomEvent('theme:loading:end', { bubbles: true })
    );
  };

  const renderStrictSearch = async ({ scrollToResults = false } = {}) => {
    const buyerUrl = new URL(window.location.href);

    if (!SEARCH_PATH_RE.test(buyerUrl.pathname)) {
      setPending(false);
      clearFailsafe();
      return false;
    }

    const buyerQuery = getBuyerQuery(buyerUrl);

    if (!shouldUseStrictSearch(buyerQuery)) {
      setPending(false);
      clearFailsafe();
      syncSearchInputs(buyerQuery);
      return false;
    }

    const section = getSearchSection();
    if (!section) {
      setPending(false);
      clearFailsafe();
      return false;
    }

    const sectionId = section.id.replace(/^shopify-section-/u, '');
    const technicalQuery = buildStrictQuery(buyerQuery);
    const requestUrl = buildInternalRequestUrl(buyerUrl, sectionId, technicalQuery);
    const currentVersion = ++renderVersion;

    if (renderController) {
      renderController.abort();
    }

    renderController = new AbortController();
    setPending(true);

    try {
      const response = await fetch(requestUrl.toString(), {
        method: 'GET',
        credentials: 'same-origin',
        headers: {
          'X-Requested-With': 'XMLHttpRequest'
        },
        signal: renderController.signal
      });

      if (!response.ok) {
        throw new Error(`Strict search render failed with HTTP ${response.status}`);
      }

      const html = await response.text();

      if (currentVersion !== renderVersion) {
        return false;
      }

      const freshSection = getSearchSection();
      if (!freshSection) {
        throw new Error('Strict search section was not found before replacement.');
      }

      const nextHtml = extractSectionHtml(html, sectionId);
      if (!normalizeWhitespace(nextHtml)) {
        throw new Error('Strict search returned an empty section.');
      }

      const staging = document.createElement('div');
      staging.innerHTML = nextHtml;
      sanitizeRenderedSection(staging, technicalQuery, buyerQuery);

      freshSection.innerHTML = staging.innerHTML;
      syncSearchInputs(buyerQuery);
      sanitizeRenderedSection(freshSection, technicalQuery, buyerQuery);

      setPending(false);
      clearFailsafe();
      dispatchThemeLoadingEnd();

      if (scrollToResults) {
        window.requestAnimationFrame(() => {
          const target = getSearchSection();
          if (target) {
            target.scrollIntoView({ behavior: 'auto', block: 'start' });
          }
        });
      }

      return true;
    } catch (error) {
      if (error && error.name === 'AbortError') {
        return false;
      }

      console.error('[Essence Strict Search]', error);
      setPending(false);
      clearFailsafe();
      syncSearchInputs(buyerQuery);
      return false;
    }
  };

  const handleSearchSectionClick = (event) => {
    if (event.defaultPrevented || event.button !== 0) {
      return;
    }

    const target = event.target;
    if (!(target instanceof Element)) {
      return;
    }

    const anchor = target.closest(`${SEARCH_SECTION_SELECTOR} a[href]`);
    if (!(anchor instanceof HTMLAnchorElement)) {
      return;
    }

    let targetUrl;
    try {
      targetUrl = new URL(anchor.href, window.location.origin);
    } catch (error) {
      return;
    }

    const currentUrl = new URL(window.location.href);
    const buyerQuery = getBuyerQuery(currentUrl);

    if (
      targetUrl.origin !== window.location.origin ||
      !SEARCH_PATH_RE.test(targetUrl.pathname) ||
      !targetUrl.searchParams.has('page') ||
      !shouldUseStrictSearch(buyerQuery)
    ) {
      return;
    }

    const cleanTarget = cleanSearchUrl(targetUrl, buyerQuery);

    // Preserve native new-tab / copy-link behavior with a clean buyer-facing URL.
    anchor.href = cleanTarget.toString();

    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    window.history.pushState(
      { essenceStrictSearch: true },
      '',
      `${cleanTarget.pathname}${cleanTarget.search}${cleanTarget.hash}`
    );

    void renderStrictSearch({ scrollToResults: true });
  };

  const installPredictiveSearchGuard = () => {
    if (window.__essenceStrictSearchFetchWrapped) {
      return;
    }

    window.__essenceStrictSearchFetchWrapped = true;

    const nativeFetch = window.fetch.bind(window);

    window.fetch = (input, init) => {
      try {
        const inputUrl =
          input instanceof Request
            ? input.url
            : input instanceof URL
              ? input.toString()
              : String(input);

        const url = new URL(inputUrl, window.location.href);

        if (
          url.origin === window.location.origin &&
          PREDICTIVE_PATH_RE.test(url.pathname) &&
          normalizeWhitespace(url.searchParams.get('q'))
        ) {
          url.searchParams.set('resources[options][fields]', PREDICTIVE_FIELDS);

          if (input instanceof Request) {
            input = new Request(url.toString(), input);
          } else {
            input = url.toString();
          }
        }
      } catch (error) {
        // Predictive search must fail open to the theme's native request.
      }

      return nativeFetch(input, init);
    };
  };

  const init = () => {
    installPredictiveSearchGuard();

    document.addEventListener('click', handleSearchSectionClick, true);

    window.addEventListener('popstate', () => {
      if (SEARCH_PATH_RE.test(window.location.pathname)) {
        void renderStrictSearch();
      }
    });

    if (SEARCH_PATH_RE.test(window.location.pathname)) {
      void renderStrictSearch();
    } else {
      setPending(false);
      clearFailsafe();
    }
  };

  // Exposed only for QA/debugging in the unpublished theme preview.
  window.EssenceStrictSearch = Object.freeze({
    version: '1.0.0',
    normalizeWhitespace,
    isSkuLikeQuery,
    tokenizeTextQuery,
    buildStrictQuery,
    shouldUseStrictSearch,
    render: renderStrictSearch
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
